import { useCallback, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'

import { ItemData } from '~/types'
import { EnableDownloadApiResponse, ItemWishlistApiResponse } from '~/types/api'
import { useSelector, useDispatch } from '~/lib/hooks/redux'
import { useToast } from '~/lib/hooks/toast'
import { getAccountData } from '~/state/ducks/accounts/selectors'
import { setDownload, resetDownload } from '~/state/ducks/download/operations'
import axios from '~/lib/axios'
import { addToWishlistEvent } from '~/lib/event'

import Button from '~/components/atoms/Button'
import ManageIsAdultModal from '~/components/organisms/ManageIsAdultModal'
import SignupModal from '~/components/organisms/SignupModal'
import PurchaseModal from '~/components/organisms/PurchaseModal'
import IconButton from '~/components/molecules/IconButton'
import { Favorite, FavoriteBorder } from '@material-ui/icons'

interface Props {
  item: ItemData
}

export default function Private({ item }: Props) {
  const { itemUid } = item
  const account = useSelector(getAccountData)
  const dispatch = useDispatch()
  const [_, setToast] = useToast()

  const purchaseAbleWithLogin = !item.isAdult || (item.isAdult && account?.isAdultEnabled)
  const [hasDownload, setHasDownload] = useState<boolean>()
  const [containsWishList, setContainsWishList] = useState<boolean | null | undefined>()
  const [clickAbleWishList, setClickAbleWishList] = useState<boolean>(true)

  const fetchContainsWishList = useCallback(async () => {
    if (itemUid !== undefined) {
      const res = await axios<boolean>(
        {
          url: `/items/${encodeURI(`${itemUid}`)}/wishlist`
        },
        { dispatch }
      )
      setContainsWishList(res)
    }
  }, [itemUid, containsWishList])

  useEffect(() => {
    //ログインしていたら、欲しい物リストに入っているかどうか判定する
    if (itemUid !== undefined && account !== undefined && account !== null) {
      fetchContainsWishList()
    } else if (account === null) {
      setContainsWishList(null)
    }
  }, [itemUid, account, containsWishList])

  const handleWishlist = useCallback(async () => {
    setClickAbleWishList(false)

    try {
      const res = await axios<ItemWishlistApiResponse>(
        {
          method: 'post',
          url: `/items/${item.itemUid}/wishlist`
        },
        { dispatch }
      )

      if (res.success) {
        if (!containsWishList) {
          addToWishlistEvent(item)
        }

        setToast(res.toastText, 'Success')
        setContainsWishList(!containsWishList)
        setClickAbleWishList(true)
      } else {
        setToast('処理中にエラーがおきました', 'Failure')
      }
    } catch (e) {
      console.error(e.code)
      console.error(e.message)
    }
  }, [itemUid, containsWishList])

  // 受け取った人用ダウンロードリンク表示
  const fetchItemDownloadAble = useCallback(async () => {
    if (itemUid !== undefined) {
      const { enabled, fileInfo } = await axios<EnableDownloadApiResponse>(
        {
          url: `/items/${encodeURI(`${itemUid}`)}/enable_download`
        },
        { dispatch }
      )

      setHasDownload(enabled)
      if (enabled) {
        dispatch(setDownload({ files: fileInfo, item }))
      }
    }
  }, [itemUid])

  useEffect(() => {
    // ログインしていたら、受け取った作品かどうか布教した作品かどうか判定する
    if (itemUid !== undefined && account !== undefined && account !== null) {
      fetchItemDownloadAble()
    } else if (account === null) {
      setHasDownload(false)
    }
    return () => {
      dispatch(resetDownload())
    }
  }, [itemUid, account])

  // 非公開もしくはすでにダウンロードできる状態なら購入系UIは表示しない
  if (!item.public || hasDownload === undefined || hasDownload) {
    return null
  }

  // 自分用に購入ボタン
  return (
    <PrivatePurchaseButtonContainer>
      {account !== undefined && account !== null ? (
        <>
          {purchaseAbleWithLogin ? (
            // R18設定に問題なければ、購入モーダル
            <PurchaseModal item={item} type='private'>
              <Button
                label='自分用に購入'
                color='secondary'
                onClick={() => {}}
                // まだ本番では購入できないようにしている
                disabled={process.env.NEXT_PUBLIC_ENABLE_PURCHASE !== 'true'}
              />
            </PurchaseModal>
          ) : (
            // R18設定に問題あれば、設定を変更モーダル
            <ManageIsAdultModal>
              <Button label='自分用に購入' color='secondary' onClick={() => {}} />
            </ManageIsAdultModal>
          )}
          <StyledIconButton
            onClick={handleWishlist}
            Icon={containsWishList ? <Favorite /> : <FavoriteBorder />}
            size={56}
            disabled={!clickAbleWishList}
          />
        </>
      ) : (
        //未ログインの場合は、ログインモーダル
        <>
          <SignupModal>
            <Button label='自分用に購入' color='secondary' onClick={() => {}} />
          </SignupModal>
          <SignupModal>
            <IconButton Icon={<FavoriteBorder />} size={56} />
          </SignupModal>
        </>
      )}
    </PrivatePurchaseButtonContainer>
  )
}

const PrivatePurchaseButtonContainer = styled.div`
  margin-top: 16px;
  display: grid;
  grid-template-columns: 1fr auto;
  grid-gap: 8px;
`
const StyledIconButton = styled(IconButton)`
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 1;
    `};
`
